import { ModuleWithProviders, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { StoreModule, ActionReducerMap, ActionReducer, Action } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { environment } from '../environments/environment';
import { SearchBoardState } from '@store/search-board/search-board.model';
import { SearchBoardReducer } from '@store/search-board/search-board.reducer';
import { UserState } from '@store/user/user.model';
import { UserReducer } from '@store/user/user.reducer';
import { UserEffects } from '@store/user/user.effects';
import { SearchEffects } from '@store/search/search.effects';
import { ExportEffects } from '@store/export/export.effects';
import { CustomHeadersEffects } from '@store/custom-headers/custom-headers.effects';
import { PreferencesEffects } from '@store/preferences/preferences.effects';
import { SearchBoardEffects } from '@store/search-board/search-board.effects';
import { LabelsEffects } from '@store/labels/labels.effects';
import { ReportsState } from '@store/reports/reports.model';
import { ReportsReducer } from '@store/reports/reports.reducer';
import { ReportsEffects } from '@store/reports/reports.effects';
import { SearchBoardActionBarEffects } from '@store/search-board-action-bar/search-board-action-bar.effects';
import { PurchaseEffect } from '@store/purchase/purchase.effect';
import { SavedPropertiesState } from '@store/saved-properties/saved-properties.model';
import { SavedPropertiesReducer } from '@store/saved-properties/saved-properties.reducer';
import { SavedPropertiesEffects } from '@store/saved-properties/saved-properties.effects';
import { SettingsReducer } from '@store/settings/settings.reducer';
import { SettingsState } from '@store/settings/settings.model';
import { OrdersState } from '@store/orders/orders.model';
import { OrdersReducer } from '@store/orders/orders.reducer';
import { OrdersEffects } from '@store/orders/orders.effects';
import { LOGOUT_SUCCESS } from '@store/user/user.actions';
import { WhatIsNewState } from '@store/what-is-new/what-is-new.model';
import { WhatIsNewReducer } from '@store/what-is-new/what-is-new.reducer';
import { WhatIsNewEffects } from '@store/what-is-new/what-is-new.effects';
import { getSubheaderMockStateOverride, isSubheaderMockEnabled } from '@shared/components/subheader/subheader-mock.service';
import { PropertyDetailsShareState } from '@store/property-details-share/property-details-share.model';
import { propertyDetailsShareReducer } from '@store/property-details-share/property-details-share.reducer';
import { PropertyDetailsShareEffects } from '@store/property-details-share/property-details-share.effects';
import { SharedReportEmailState } from '@store/shared-report-email/shared-report-email.model';
import { sharedReportEmailReducer } from '@store/shared-report-email/shared-report-email.reducer';
import { SharedReportEmailEffects } from '@store/shared-report-email/shared-report-email.effects';

export interface AppState {
  searchBoard: SearchBoardState;
  user: UserState;
  reports: ReportsState;
  savedProperties: SavedPropertiesState;
  settings: SettingsState;
  orders: OrdersState;
  whatIsNew: WhatIsNewState;
  propertyDetailsShare?: PropertyDetailsShareState;
  sharedReportEmail?: SharedReportEmailState;
}

export const reducers: ActionReducerMap<AppState> = {
  searchBoard: SearchBoardReducer,
  user: UserReducer,
  reports: ReportsReducer,
  savedProperties: SavedPropertiesReducer,
  settings: SettingsReducer,
  orders: OrdersReducer,
  whatIsNew: WhatIsNewReducer,
  propertyDetailsShare: propertyDetailsShareReducer,
  sharedReportEmail: sharedReportEmailReducer
};

export function storeClear(reducer: ActionReducer<AppState>): ActionReducer<AppState> {
  return (state: AppState, action: Action): AppState => {
    let nextState = state;
    if (action.type === LOGOUT_SUCCESS) {
      nextState = undefined;
      sessionStorage.removeItem('hmrState');
    }
    return reducer(nextState, action);
  };
}

/**
 * Meta-reducer that seeds store slices with the last captured
 * subheader snapshot from localStorage on the very first store init.
 * Toggle on/off via browser console: subheaderMock.enable() / subheaderMock.disable()
 *
 * (Development only)
 */
export function subheaderMockMetaReducer(reducer: ActionReducer<AppState>): ActionReducer<AppState> {
  let initialized = false;
  return (state: AppState, action: Action): AppState => {
    if (!initialized) {
      initialized = true;
      if (isSubheaderMockEnabled()) {
        const override = getSubheaderMockStateOverride();
        if (override) {
          state = Object.keys(override).reduce((acc, key) => ({
            ...acc,
            [key]: { ...acc?.[key], ...override[key] }
          }), { ...state });
        }
      }
    }
    return reducer(state, action);
  };
}


@NgModule({
  declarations: [],
  imports: [
    CommonModule,
    StoreModule.forRoot(reducers, {
      runtimeChecks: {
        strictStateSerializability: true,
        strictActionSerializability: true,
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    !environment.production ? StoreDevtoolsModule.instrument(
      {
        maxAge: 25,
      connectInZone: true}
    ) : [],
    EffectsModule.forRoot([
      UserEffects,
      SearchEffects,
      ExportEffects,
      LabelsEffects,
      CustomHeadersEffects,
      PreferencesEffects,
      SearchBoardEffects,
      SearchBoardActionBarEffects,
      PurchaseEffect,
      ReportsEffects,
      SavedPropertiesEffects,
      OrdersEffects,
      WhatIsNewEffects,
      PropertyDetailsShareEffects,
      SharedReportEmailEffects
    ])
  ]
})
export class AppStateModule {
  static forRoot(): ModuleWithProviders<any> {
    return {
      ngModule: AppStateModule,
      providers: [
        // ─── Subheader mock — comment out to disable ──────────────────────
        // { provide: META_REDUCERS, useValue: subheaderMockMetaReducer, multi: true },
      ]
    };
  }
}
